import { css, keyframes } from '@emotion/react';
import Link, { LinkProps } from 'next/link';
import React from 'react';

import { LoadingIndicator } from '@/ui/controls';

import { EnnismoreTheme, useTheme } from '../theme';

/**
 * NOTE: These button variants do nothing. Currently they're used only by the My Account / Amend booking flows.
 * We're removing the variant styles themselves with an expectation that we'll re-introduce them with
 * the new theming system.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'destructive';

export const buttonStyles = ({
  buttons,
  colors,
  variant,
}: {
  buttons: EnnismoreTheme['buttons'];
  colors: EnnismoreTheme['colors'];
  variant: ButtonVariant;
}) => {
  const styles: EnnismoreTheme['buttons']['primary'] =
    buttons[variant] ?? buttons.primary;
  return css({
    fontFamily: styles.fontFamily,
    appearance: 'none',
    display: 'inline-block',
    textAlign: 'center',
    textDecoration: 'none',
    fontSize: styles.fontSize ?? '16px',
    lineHeight: styles.lineHeight ?? '24px',
    fontWeight: styles.fontWeight ?? 'bold',
    padding: styles.padding ?? '12px 20px',
    borderRadius:
      typeof styles.borderRadius === 'undefined' ? 4 : styles.borderRadius,
    transition: styles.transition ?? 'opacity ease .1s',
    cursor: 'pointer',
    position: 'relative',
    width: styles.width ?? '100%',
    minWidth: styles.minWidth ?? '100%',
    color: styles.color ?? colors.white,
    backgroundColor: styles.backgroundColor,
    textTransform: styles.textTransform,
    touchAction: 'manipulation',
    letterSpacing: styles.letterSpacing,
    border: styles.border,
    mixBlendMode: styles.mixBlendMode,

    ':hover': { opacity: 0.85, ...styles[':hover'] },

    ':active:not(:disabled)': {
      opacity: 1.0,
    },

    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
      ...styles[':disabled'],
    },

    '&.focus-visible': {
      outline: `1px solid ${colors.secondary300}`,
      outlineOffset: 4,
    },
  });
};

export interface ButtonProps {
  children: string;
  onClick?: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  variant?: ButtonVariant;
  testId?: string;
  className?: string;
}

export const Button = ({
  children,
  onClick,
  isDisabled,
  isLoading,
  type,
  variant = 'primary',
  testId,
  className,
}: ButtonProps) => {
  const { colors, buttons } = useTheme();

  return (
    <button
      css={buttonStyles({ buttons, colors, variant })}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled || isLoading}
      type={type ?? 'button'}
      aria-label={children}
      data-test={testId}
      className={className}
    >
      {isLoading && (
        <div
          css={css({
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          })}
        >
          <LoadingIndicator
            style={buttons[variant]?.loadingIndicatorStyle}
            width={25}
          />
        </div>
      )}

      <div
        css={css({
          visibility: isLoading ? 'hidden' : 'visible',
        })}
      >
        {children}
      </div>
    </button>
  );
};

type SubmitButtonProps = Omit<ButtonProps, 'onClick' | 'type'>;
export const SubmitButton: React.FC<SubmitButtonProps> = (props) => (
  // Shoehorning this in a bit here.
  // Obviously passing an onClick handler isn't really appropriate for a button with type=submit, but I want to keep it required unless we use a specific component like this.
  <Button onClick={null as any} type="submit" {...props} />
);

export interface ButtonLinkProps extends React.PropsWithChildren<LinkProps> {}
export const ButtonLink: React.FC<ButtonLinkProps> = ({
  children,
  ...props
}) => {
  const { buttons, colors } = useTheme();
  return (
    <Link
      {...props}
      css={buttonStyles({ buttons, colors, variant: 'primary' })}
    >
      {children}
    </Link>
  );
};

export default Button;
